/** Step 3, Structure: per roof face framing and roofing (faces listed and highlighted on a small roof map), the
 * site loads and racking in the country's units, and the screening results per face with one-click fixes. */
import { Copy, Wrench } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge, Card, Field, NumberInput, Select, TextInput, cx } from "../components/ui";
import type { MessageKey } from "../i18n/keys";
import { useT } from "../i18n/I18nProvider";
import { STATUS_LABEL, STATUS_TONE, type CheckStatus } from "../lib/permits";
import { attachmentFix, formatLength, fromDisplay, lengthUnit, toDisplay, type FaceResult } from "../lib/permitWorkspace";
import { AttachmentFields } from "./Documents";
import { SitePlan, planShapes } from "./SitePlan";
import { HazardLoads } from "./HazardLoads";
import { StepChecks, StepHead, useFocusAnchor, type Ws } from "./PermitWorkspace";
import { ZoneDetails } from "./ZoneDetails";
import { useFormat } from "./format";
import type { S } from "../api/client";

type Option = { value: string; label: MessageKey };
const FRAMING: Option[] = [
  { value: "", label: "permit.structure.notEntered" },
  { value: "rafter", label: "permit.structure.framing.rafter" },
  { value: "truss", label: "permit.structure.framing.truss" },
  { value: "other", label: "permit.structure.framing.other" },
];
const MATERIALS: Option[] = [
  { value: "", label: "permit.structure.notEntered" },
  { value: "asphalt_shingle", label: "permit.structure.material.asphaltShingle" },
  { value: "metal_standing_seam", label: "permit.structure.material.metalStandingSeam" },
  { value: "metal_corrugated", label: "permit.structure.material.metalCorrugated" },
  { value: "concrete_tile", label: "permit.structure.material.concreteTile" },
  { value: "clay_tile", label: "permit.structure.material.clayTile" },
  { value: "slate", label: "permit.structure.material.slate" },
  { value: "wood_shake", label: "permit.structure.material.woodShake" },
  { value: "membrane", label: "permit.structure.material.membrane" },
  { value: "other", label: "permit.structure.material.other" },
];
/** Product names stay as they are; the generic rail is translated. */
const RAILS: { value: string; label?: MessageKey; name?: string }[] = [
  { value: "", label: "permit.structure.rail.generic" },
  { value: "ironridge_xr10", name: "IronRidge XR10" },
  { value: "ironridge_xr100", name: "IronRidge XR100" },
  { value: "unirac_solarmount", name: "Unirac SolarMount" },
  { value: "generic", label: "permit.structure.rail.generic" },
];
const LUMBER: Option[] = [
  { value: "", label: "permit.structure.lumber.notEntered" },
  { value: "df_l_2", label: "permit.structure.lumber.dfl2" },
  { value: "df_l_1", label: "permit.structure.lumber.dfl1" },
  { value: "hf_2", label: "permit.structure.lumber.hf2" },
  { value: "spf_2", label: "permit.structure.lumber.spf2" },
  { value: "sp_2", label: "permit.structure.lumber.sp2" },
];
const KPA_TO_PSF = 20.885434;
const KG_M2_TO_PSF = 0.204816;

export function StructureStep({ ws }: { ws: Ws }) {
  const t = useT();
  const f = useFormat();
  const opts = (list: Option[]) => list.map((o) => ({ value: o.value, label: t(o.label) }));
  const shapes = useMemo(() => planShapes(ws.doc), [ws.doc]);
  const faces = ws.site?.faces ?? [];
  const [faceId, setFaceId] = useState<string | null>(faces[0]?.id ?? null);
  useFocusAnchor(ws.focus, (anchor) => {
    if (anchor.startsWith("face:")) setFaceId(anchor.slice(5));
  });
  const results = (ws.preview.data?.electrical.structural?.faces ?? []) as (FaceResult & Record<string, unknown>)[];
  const statusOf = Object.fromEntries(results.map((r) => [r.face_id, r.status]));
  const units = ws.units;
  const imperial = units === "imperial";
  const off = !ws.canEdit;
  const st = ws.details.structural ?? {};
  const setSt = (patch: Partial<S["PermitStructuralInputs"]>) => ws.setDetails((d) => ({ ...d, structural: { ...(d.structural ?? {}), ...patch } }));
  const face = faces.find((f) => f.id === (faceId ?? faces[0]?.id));
  const fd = (face && ws.details.faces?.[face.id]) || {};
  const setFace = (patch: Partial<S["PermitFaceDetails"]>) =>
    face && ws.setDetails((d) => ({ ...d, faces: { ...(d.faces ?? {}), [face.id]: { ...(d.faces?.[face.id] ?? {}), ...patch } } }));
  const copyToAll = () =>
    face &&
    ws.setDetails((d) => {
      const { attachment_spacing_m: _, notes: __, ...shared } = d.faces?.[face.id] ?? {};
      return { ...d, faces: Object.fromEntries(faces.map((f) => [f.id, f.id === face.id ? d.faces?.[f.id] ?? {} : { ...(d.faces?.[f.id] ?? {}), ...shared }])) };
    });
  const len = (kind: "short" | "long") => lengthUnit(units, kind);
  const pressure = (kpa: unknown) => (typeof kpa === "number" ? (imperial ? `${f.num(kpa * KPA_TO_PSF, 1)} psf` : `${f.num(kpa, 2)} kPa`) : "—");
  const force = (n: unknown) => (typeof n === "number" ? (imperial ? `${f.num(n * 0.224809)} lbf` : `${f.num(n / 1000, 2)} kN`) : "—");

  return (
    <div className="pw-step-body">
      <StepHead title={t("permit.step.structure")} description={t("permit.structure.description")} status={ws.statuses.structure} save={ws.siteSave} />
      <div className="pw-cols structure">
        <Card title={t("permit.structure.faces.title")} description={t("permit.structure.faces.description")}>
          <div className="pw-faces">
            <div className="pw-face-map">
              <SitePlan shapes={shapes} compact highlightFace={face?.id} onFace={setFaceId} faceStatus={statusOf} />
            </div>
            <ul className="pw-face-list" role="listbox" aria-label={t("permit.structure.faces.title")}>
              {faces.map((fc) => {
                const s = statusOf[fc.id] as CheckStatus | undefined;
                return (
                  <li key={fc.id}>
                    <button role="option" aria-selected={fc.id === face?.id} className={cx("pw-face-item", fc.id === face?.id && "on")} onClick={() => setFaceId(fc.id)} data-anchor={`face:${fc.id}`}>
                      <b>{t("permit.structure.face.name", { label: fc.label })}</b>
                      <span className="small muted">{t("permit.structure.face.summary", { pitch: f.num(fc.pitch_deg), count: fc.modules })}</span>
                      {s && <Badge tone={STATUS_TONE[s]}>{t(STATUS_LABEL[s])}</Badge>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          {face && (
            <div className="pw-face-form">
              <div className="spread">
                <h3>{t("permit.structure.face.name", { label: face.label })}</h3>
                {faces.length > 1 && !off && (
                  <button className="link-btn small" onClick={copyToAll}>
                    <Copy size={13} /> {t("permit.structure.copyToAll")}
                  </button>
                )}
              </div>
              <div className="pw-form-grid">
                <Field label={t("permit.structure.framing.label")}>
                  <Select value={fd.framing ?? ""} disabled={off} onChange={(v) => setFace({ framing: (v || null) as S["PermitFaceDetails"]["framing"] })} options={opts(FRAMING)} ariaLabel={t("permit.structure.framing.label")} />
                </Field>
                <Field label={t("permit.structure.memberSize.label")}>
                  <TextInput value={fd.member_size ?? ""} disabled={off} maxLength={40} placeholder={imperial ? t("permit.structure.memberSize.placeholderImperial") : t("permit.structure.memberSize.placeholderMetric")} onChange={(e) => setFace({ member_size: e.target.value || null })} />
                </Field>
                <Field label={t("permit.structure.spacing.label")}>
                  <NumberInput value={toDisplay(fd.spacing_m, units)} disabled={off} onChange={(v) => setFace({ spacing_m: fromDisplay(v, units) })} suffix={len("short")} placeholder={imperial ? t("permit.structure.spacing.placeholderImperial") : t("permit.structure.spacing.placeholderMetric")} />
                </Field>
                <Field label={t("permit.structure.span.label")}>
                  <NumberInput value={toDisplay(fd.span_m, units, "long")} disabled={off} onChange={(v) => setFace({ span_m: fromDisplay(v, units, "long") })} suffix={len("long")} />
                </Field>
                {imperial && (
                  <Field label={t("permit.structure.lumber.label")} hint={t("permit.structure.lumber.hint")}>
                    <Select value={fd.lumber ?? ""} disabled={off} onChange={(v) => setFace({ lumber: (v || null) as S["PermitFaceDetails"]["lumber"] })} options={opts(LUMBER)} ariaLabel={t("permit.structure.lumber.label")} />
                  </Field>
                )}
                <Field label={t("permit.structure.material.label")}>
                  <Select value={fd.roof_material ?? ""} disabled={off} onChange={(v) => setFace({ roof_material: (v || null) as S["PermitFaceDetails"]["roof_material"] })} options={opts(MATERIALS)} ariaLabel={t("permit.structure.material.label")} />
                </Field>
                <Field label={t("permit.structure.deck.label")}>
                  <TextInput value={fd.deck ?? ""} disabled={off} maxLength={80} placeholder={imperial ? t("permit.structure.deck.placeholderImperial") : t("permit.structure.deck.placeholderMetric")} onChange={(e) => setFace({ deck: e.target.value || null })} />
                </Field>
                <Field label={t("permit.structure.attachmentSpacing.label")} hint={t("permit.structure.attachmentSpacing.hint")}>
                  <NumberInput value={toDisplay(fd.attachment_spacing_m, units)} disabled={off} onChange={(v) => setFace({ attachment_spacing_m: fromDisplay(v, units) })} suffix={len("short")} placeholder={t("permit.structure.attachmentSpacing.auto")} />
                </Field>
                <Field label={t("permit.structure.notes.label")}>
                  <TextInput value={fd.notes ?? ""} disabled={off} maxLength={300} onChange={(e) => setFace({ notes: e.target.value || null })} />
                </Field>
              </div>
            </div>
          )}
          {(ws.site?.orphaned_faces?.length ?? 0) > 0 && (
            <p className="small muted">{t("permit.structure.orphanedFaces", { count: ws.site!.orphaned_faces!.length })}</p>
          )}
        </Card>

        <Card title={t("permit.structure.loads.title")} description={imperial ? t("permit.structure.loads.descriptionUs") : t("permit.structure.loads.descriptionOther")}>
          <HazardLoads ws={ws} set={setSt} />
          <div className="pw-form-grid">
            <Field label={t("permit.structure.rackingWeight.label")} hint={t("permit.structure.rackingWeight.hint", { kg: 2.5, psf: 0.5 })}>
              <NumberInput
                value={st.racking_weight_kg_m2 == null ? null : imperial ? Math.round(st.racking_weight_kg_m2 * KG_M2_TO_PSF * 100) / 100 : st.racking_weight_kg_m2}
                disabled={off}
                onChange={(v) => setSt({ racking_weight_kg_m2: v == null ? null : imperial ? Math.round((v / KG_M2_TO_PSF) * 100) / 100 : v })}
                suffix={imperial ? "psf" : "kg/m²"}
              />
            </Field>
          </div>
          <div className="pw-form-grid" data-anchor="rail">
            <Field label={t("permit.structure.rail.label")}>
              <Select
                value={st.rail ?? ""}
                disabled={off}
                onChange={(v) => setSt({ rail: (v || null) as S["PermitStructuralInputs"]["rail"] })}
                options={RAILS.slice(0, 4).map((r) => ({ value: r.value, label: r.name ?? t(r.label!) }))}
                ariaLabel={t("permit.structure.rail.label")}
              />
            </Field>
            <Field label={t("permit.structure.railSpan.label")} hint={t("permit.structure.railSpan.hint")}>
              <NumberInput value={toDisplay(st.rail_max_span_m, units)} disabled={off} onChange={(v) => setSt({ rail_max_span_m: fromDisplay(v, units) })} suffix={len("short")} />
            </Field>
          </div>
          <AttachmentFields ws={ws} />
          <StepChecks ws={ws} step="structure" />
        </Card>
      </div>

      <Card title={t("permit.structure.results.title")} description={t("permit.structure.results.description")}>
        <div className={cx("pw-table-wrap", ws.preview.isFetching && "pw-stale")}>
          <table className="pw-table">
            <thead>
              <tr>
                <th>{t("permit.structure.results.face")}</th>
                <th>{t("permit.structure.results.result")}</th>
                <th className="num">{t("permit.structure.results.designPressure")}</th>
                <th className="num">{t("permit.structure.results.rafters")}</th>
                <th className="num">{t("permit.structure.results.allowedSpan")}</th>
                <th className="num">{t("permit.structure.results.attachments")}</th>
                <th className="num">{t("permit.structure.results.upliftEach")}</th>
                <th>{t("permit.structure.notes.label")}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => {
                const fix = r.status === "problem" && ws.canEdit ? attachmentFix(r) : null;
                return (
                  <tr key={r.face_id} className={cx(r.face_id === face?.id && "sel")} onClick={() => setFaceId(r.face_id)}>
                    <td><b>{r.label}</b></td>
                    <td><Badge tone={STATUS_TONE[r.status]}>{r.status === "info" ? t("permit.structure.results.notChecked") : t(STATUS_LABEL[r.status])}</Badge></td>
                    <td className="num">{pressure(r.design_kpa)}</td>
                    <td className="num">{formatLength(r.rafter_spacing_m, units, f.digits)}</td>
                    <td className="num">{formatLength(r.allowed_span_m, units, f.digits)}</td>
                    <td className="num">
                      {formatLength(r.attachment_spacing_m, units, f.digits)}
                      {typeof r.rafters_per_span === "number" && r.rafters_per_span > 0 ? <div className="small muted">{t("permit.structure.results.everyRafters", { count: r.rafters_per_span })}</div> : null}
                    </td>
                    <td className="num">{force(r.uplift_per_attachment_n)}</td>
                    <td className="small">
                      <ZoneDetails face={r} units={units} />
                      {(r.notes ?? []).join("; ") || ((r.zones as unknown[] | undefined)?.length ? null : "—")}
                      {fix != null && (
                        <div>
                          <button
                            className="pw-mini fix"
                            onClick={(e) => {
                              e.stopPropagation();
                              ws.setDetails((d) => ({ ...d, faces: { ...(d.faces ?? {}), [r.face_id]: { ...(d.faces?.[r.face_id] ?? {}), attachment_spacing_m: fix } } }));
                            }}
                          >
                            <Wrench size={13} /> {t("permit.structure.results.setSpacing", { spacing: formatLength(fix, units, f.digits) })}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!results.length && (
                <tr>
                  <td colSpan={8} className="muted">{t("permit.structure.results.computing")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
